import { HIDDEN_SELECTOR } from '../utils/keyboard-nav-support';
import { registerDestructor } from '@ember/destroyable';
import Modifier from 'ember-modifier';

export const stripBolding = function (element) {
  let strongElements = element.querySelectorAll('strong');

  Array.from(strongElements).forEach(strong => {
    let textNode = element.ownerDocument.createTextNode(strong.textContent);
    let parentNode = strong.parentNode || strong.parentElement;

    if (!parentNode) return;

    parentNode.replaceChild(textNode, strong);
    parentNode.textContent = parentNode.textContent?.replace(/ +/g, ' ');
  });

  element?.normalize();

  return element?.textContent?.trim();
};

export default class BoldMatchingText extends Modifier {
  allNodes = [];
  element = null;
  upperSearchTerm = null;
  walker = null;

  #applyBolding = () => {
    this.#createWalker();

    this.#getNodes();

    this.#processNodes().reverse().forEach(this.#processSingleNode);
  };

  #createAndPopulateStrongElement = (
    matchText,
    position,
    needsLeadingSpace,
    needsTrailingSpace,
    trimmedLength
  ) => {
    let isAtBeginning = position === 0;
    let isAtEnd = position + this.upperSearchTerm.length >= trimmedLength;
    let prefix = needsLeadingSpace && !isAtBeginning ? ' ' : '';
    let strongElement = this.element.ownerDocument.createElement('strong');
    let suffix = needsTrailingSpace && !isAtEnd ? ' ' : '';

    strongElement.style.whiteSpace = 'pre-wrap';
    strongElement.textContent = prefix + matchText + suffix;

    return strongElement;
  };

  #createWalker = () => {
    this.allNodes = [];
    this.walker = this.element.ownerDocument.createTreeWalker(
      this.element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
  };

  #getNodes = () => {
    let textNode;

    while ((textNode = this.walker.nextNode())) {
      if (textNode.nodeValue.trim() === '') {
        continue;
      }

      this.allNodes.push(textNode);
    }
  };

  #processNodes = () => {
    let matches = [];

    this.allNodes.forEach(textNode => {
      let originalNodeValue = textNode.nodeValue;
      let trimmedNodeValue = originalNodeValue.trim();
      let upperText = trimmedNodeValue.toUpperCase();

      if (upperText.includes(this.upperSearchTerm)) {
        let position = upperText.indexOf(this.upperSearchTerm);
        let match = {
          offset: originalNodeValue.indexOf(trimmedNodeValue),
          positions: [position],
          textNode,
          trimmedNodeLength: trimmedNodeValue.length,
        };

        while ((position = upperText.indexOf(this.upperSearchTerm, position + 1)) !== -1) {
          match.positions.push(position);
        }

        matches.push(match);
      }
    });

    return matches;
  };

  #processSingleNode = ({ offset, positions, textNode, trimmedNodeLength }) => {
    let matchLength = this.upperSearchTerm.length;

    positions.reverse().forEach(position => {
      let actualPosition = offset + position;
      let originalTextContent = textNode.nodeValue;
      let beforeMatchPos = actualPosition - 1;
      let afterMatchPos = actualPosition + matchLength;
      let matchStartNode = textNode.splitText(actualPosition);

      matchStartNode.splitText(Math.min(matchLength, matchStartNode.nodeValue.length));

      let matchText = matchStartNode.nodeValue;
      let needsLeadingSpace = false;
      let needsTrailingSpace = false;

      if (beforeMatchPos >= 0 && actualPosition > 0) {
        needsLeadingSpace =
          originalTextContent[beforeMatchPos] === ' ' && !matchText.startsWith(' ');
      }

      if (afterMatchPos < originalTextContent.length) {
        needsTrailingSpace = originalTextContent[afterMatchPos] === ' ' && !matchText.endsWith(' ');
      }

      let strongElement = this.#createAndPopulateStrongElement(
        matchText,
        position,
        needsLeadingSpace,
        needsTrailingSpace,
        trimmedNodeLength
      );

      matchStartNode.parentNode.replaceChild(strongElement, matchStartNode);
    });
  };

  #stripBolding = () => {
    stripBolding(this.element);
  };

  cleanup = () => {
    this.allNodes = [];
    this.element = null;
    this.upperSearchTerm = null;
    this.walker = null;
  };

  modify = (element, [searchTerm]) => {
    this.element = element.querySelector('span') ?? element;

    if (!searchTerm || this.element.classList.contains(HIDDEN_SELECTOR)) return;

    this.upperSearchTerm = searchTerm.trim().toUpperCase();

    this.#stripBolding();
    this.#applyBolding();

    registerDestructor(this, this.cleanup);
  };
}
